import { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';

import ListUploader from './components/ListUploader';
import ClassifiedListViewer from './components/ClassifiedListViewer';

const ContactListsManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [contactLists, setContactLists] = useState([
    {
      id: 'list-1',
      name: 'Premium Customers Q1 2026',
      totalContacts: 8920,
      tierBreakdown: {
        platinum: 6234,
        gold: 2686,
        silver: 0,
        bronze: 0,
        lead: 0
      },
      engagementScore: 95,
      verificationCoverage: 100,
      deliverabilityRate: 98.7,
      lastUpdated: '2026-02-20T10:30:00Z',
      createdAt: '2026-01-15T08:00:00Z'
    },
    {
      id: 'list-2',
      name: 'Active Subscribers',
      totalContacts: 15420,
      tierBreakdown: {
        platinum: 0,
        gold: 15420,
        silver: 0,
        bronze: 0,
        lead: 0
      },
      engagementScore: 88,
      verificationCoverage: 95,
      deliverabilityRate: 96.2,
      lastUpdated: '2026-02-22T14:15:00Z',
      createdAt: '2026-01-20T10:00:00Z'
    },
    {
      id: 'list-3',
      name: 'Regular Subscribers',
      totalContacts: 12340,
      tierBreakdown: {
        platinum: 0,
        gold: 0,
        silver: 12340,
        bronze: 0,
        lead: 0
      },
      engagementScore: 72,
      verificationCoverage: 88,
      deliverabilityRate: 93.5,
      lastUpdated: '2026-02-21T09:00:00Z',
      createdAt: '2026-01-25T12:00:00Z'
    },
    {
      id: 'list-4',
      name: 'Inactive Users',
      totalContacts: 6780,
      tierBreakdown: {
        platinum: 0,
        gold: 0,
        silver: 0,
        bronze: 6780,
        lead: 0
      },
      engagementScore: 58,
      verificationCoverage: 75,
      deliverabilityRate: 89.3,
      lastUpdated: '2026-02-18T16:30:00Z',
      createdAt: '2026-02-01T14:00:00Z'
    },
    {
      id: 'list-5',
      name: 'New Leads February',
      totalContacts: 23450,
      tierBreakdown: {
        platinum: 0,
        gold: 0,
        silver: 0,
        bronze: 0,
        lead: 23450
      },
      engagementScore: 0,
      verificationCoverage: 0,
      deliverabilityRate: 0,
      lastUpdated: '2026-02-24T11:00:00Z',
      createdAt: '2026-02-24T11:00:00Z'
    }
  ]);
  const [selectedList, setSelectedList] = useState(null);

  const handleListUpload = (newList) => {
    setContactLists([...contactLists, newList]);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'platinum':
        return 'bg-purple-500';
      case 'gold':
        return 'bg-yellow-500';
      case 'silver':
        return 'bg-slate-400';
      case 'bronze':
        return 'bg-orange-600';
      case 'lead':
        return 'bg-slate-600';
      default:
        return 'bg-slate-500';
    }
  };

  const getPrimaryTier = (tierBreakdown) => {
    const tiers = ['platinum', 'gold', 'silver', 'bronze', 'lead'];
    for (const tier of tiers) {
      if (tierBreakdown?.[tier] > 0) return tier;
    }
    return 'lead';
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar 
        collapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        currentSection="Contact Lists"
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onMenuClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
        
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-6 py-8">
            <Breadcrumbs
              items={[
                { label: 'Home', path: '/' },
                { label: 'Contact Lists Management', path: '/contact-lists-management' }
              ]}
            />

            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  Contact Lists Management
                </h1>
                <p className="text-muted-foreground">
                  Upload, organize, and classify your contact lists from Lead to Gold tier
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Panel - List Browser */}
              <div className="lg:col-span-1">
                <div className="bg-card rounded-lg border border-border">
                  <div className="p-4 border-b border-border">
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Your Lists
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {contactLists?.length} total lists
                    </p>
                  </div>

                  <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                    {contactLists?.map((list) => {
                      const primaryTier = getPrimaryTier(list?.tierBreakdown);
                      return (
                        <button
                          key={list?.id}
                          onClick={() => setSelectedList(list)}
                          className={`w-full p-4 text-left hover:bg-muted transition-colors ${
                            selectedList?.id === list?.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-medium text-foreground mb-1">
                                {list?.name}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white ${
                                  getTierColor(primaryTier)
                                }`}>
                                  {primaryTier?.charAt(0)?.toUpperCase() + primaryTier?.slice(1)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {list?.totalContacts?.toLocaleString()} contacts
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Icon name="TrendingUp" size={12} />
                              <span>{list?.engagementScore}%</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Icon name="CheckCircle" size={12} />
                              <span>{list?.verificationCoverage}%</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Panel - Upload and Details */}
              <div className="lg:col-span-2 space-y-6">
                {/* Upload Section */}
                <ListUploader onUpload={handleListUpload} />

                {/* Selected List Details */}
                {selectedList ? (
                  <ClassifiedListViewer list={selectedList} />
                ) : (
                  <div className="bg-card rounded-lg border border-border p-12 text-center">
                    <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                      No List Selected
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Select a list from the left panel to view details and manage contacts
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ContactListsManagement;